/**
 * Head-space — the Character panel's second tenant (ADR-017 family; the
 * ADR-019 §10 named non-goal arriving with its first specimen): one constant
 * pitch offset for the cervical chain, correcting BONE SEATING. Mixamo rests
 * its Neck→Head bone deliberately forward-pitched (73.1° above horizontal,
 * flesh compensating to read erect); Tripo rests near-vertical (81.7–86.1°
 * measured) — absolute transport copies the source's bone pitch faithfully,
 * so the target's flesh wears the difference as head-down. Measured 2026-08-01
 * (head-pitch-probe): transport delta 0.0°, seating delta +8.6°/+13.0°.
 *
 * ADR-030 (2026-08-27): head-space RIDES THE ARMATURE. The Document run
 * reseats the Neck bone's BIND (reseatBoneBind + its scene twin) after the
 * clips are retargeted, so the exported character carries the seating in its
 * inverse binds and every clip World layers onto it — ours or its own — reads
 * erect. Kernel ClipData never passes through: the clip library stays
 * generic (ADR-016). The display filter (withWorldAxisPreRotation) is now
 * PREVIEW ONLY, applied to the difference between the dragged value and the
 * seating the armature already wears.
 *
 * THE WHOLE ANGLE LIVES ON THE NECK. The 40/60 Neck/Head split of the display
 * era was a knob detail; the reseat pins it, because the instrument that
 * measures the baseline (headSeatingDeltaDegrees) is the Neck→Head SEGMENT's
 * rest pitch, and only the Neck's rotation moves that segment. Seat the whole
 * angle on the Neck and a re-imported export measures a baseline of ~0 — the
 * seating it already carries is not applied a second time (the reopen loop
 * Allen tested closes by construction). Split it, and the reopened file would
 * measure the Head's share as still-owed. The Head is still resolved: it must
 * exist and DESCEND from the Neck (so it rides the reseat), and it is the
 * far end of the instrument.
 *
 * Axis: the declared SIDE (up × facing). Sign: POSITIVE degrees pitch the head
 * ERECT-ward at play. By construction of the right-handed triad, rotation
 * about +side carries up TOWARD facing — a nod down — so erect-ward is the
 * negative rotation about side; the BIND reseat is its inverse (the bone
 * pitches down at rest, so a clip playing the original pitch reads erect).
 *
 * This is the seam gaze will drive (ADR-018): the same pre-rotation door,
 * fed by a look-target direction instead of a constant, clamped by cervical
 * ROM — and gaze stays MOTION (ADR-030 §6: a constant property of the body
 * is armature; a per-frame intent never is). Do not grow gaze here.
 *
 * Depends on: math/vec3+quat, ir/rig-descriptor, ir/errors, arm-space (the
 * shared declared-axes reading + WorldAxisRotation). Depended on by: the app
 * session driver (the reseat), the app's preview filter, the probes.
 */
import { KernelError } from '../ir/errors.js';
import type { RigDescriptor, RigBone } from '../ir/rig-descriptor.js';
import {
  quatFromAxisAngle,
  quatInvertUnit,
  quatNormalize,
  quatRotateVec3,
  type Quat,
} from '../math/quat.js';
import { vec3Add, vec3Cross, vec3Dot, vec3Scale, vec3Sub } from '../math/vec3.js';
import { declaredAxes, type WorldAxisRotation } from './arm-space.js';
import { POQPOQ_EXTRAS_KEY, readPoqpoqExtra, withPoqpoqExtra } from './poqpoq-extras.js';

/**
 * The one canonical band: the Document schema gate and this derivation refuse
 * the same range, imported from the same constant (two doors, one value).
 * ±60° is the HUMAN cervical envelope, not a taste call (Allen, 2026-08-01:
 * the band must mimic human motion range) — the medical ROM DB
 * (BiomechanicalData, cervical C1–C7) puts anatomical flexion at 60°,
 * extension at 75° with safe-working 60°, so a symmetric 60 covers full
 * flexion and safe extension while the measured seating class (5–16°) sits
 * comfortably inside. The offset rides ON TOP of a clip's own neck motion,
 * so totals can still exceed anatomy — that judgement belongs to the gaze/ROM
 * arc (ADR-018), not this constant.
 */
export const HEAD_SPACE_MAX_DEGREES = 60;

/** The ontology id whose bone carries the seating (its subtree follows). */
export const HEAD_SPACE_SEAT = 'Neck';
/** The ontology id at the far end of the instrument; must descend from the seat. */
export const HEAD_SPACE_END = 'Head';

/**
 * A rig this feature cannot carry says WHY: the panel renders `reason` as its
 * disabled state, so deliberate zero and broken are visibly different. Never a
 * partial chain — one resolvable bone of the two is a refusal.
 */
export class HeadSpaceRefusal extends KernelError {
  constructor(readonly reason: string) {
    super(`head-space: ${reason}`);
  }
}

export interface HeadSpaceOffset {
  /** The rig's own bone name (the track target the display clip carries). */
  readonly bone: string;
  /** Constant parent-bind-frame pre-rotation: q_display = rotation ∘ q_track. */
  readonly rotation: Quat;
}

function resolveChainBone(
  rig: RigDescriptor,
  ontologyId: string,
): { bone: RigBone; parent: RigBone } {
  const entries = rig.mapping.filter((m) => m.ontologyId === ontologyId);
  if (entries.length === 0) {
    throw new HeadSpaceRefusal(
      `"${rig.label}" maps no bone to "${ontologyId}" — head-space needs both Neck and Head`,
    );
  }
  if (entries.length > 1) {
    throw new HeadSpaceRefusal(
      `"${ontologyId}" is ambiguous on "${rig.label}" (${entries.map((e) => e.rigBone).join(', ')}) — refusing to pick`,
    );
  }
  const name = (entries[0] as (typeof entries)[number]).rigBone;
  const bone = rig.bones.find((b) => b.name === name);
  if (bone === undefined) {
    throw new HeadSpaceRefusal(
      `mapping names "${name}" for "${ontologyId}" but "${rig.label}" carries no such bone`,
    );
  }
  if (bone.parent === null) {
    throw new HeadSpaceRefusal(`"${name}" has no parent — no frame to carry the nod plane`);
  }
  const parent = rig.bones.find((b) => b.name === bone.parent);
  if (parent === undefined) {
    throw new HeadSpaceRefusal(`"${name}"'s parent "${bone.parent}" is not in the rig`);
  }
  return { bone, parent };
}

function resolveChain(rig: RigDescriptor): {
  neck: { bone: RigBone; parent: RigBone };
  head: { bone: RigBone; parent: RigBone };
} {
  const neck = resolveChainBone(rig, HEAD_SPACE_SEAT);
  const head = resolveChainBone(rig, HEAD_SPACE_END);
  // The seating reaches the skull ONLY if Head descends from Neck — interposed
  // twist bones are fine (they ride the reseat and carry the rotation down),
  // a detached head is not: the neck would be reseated under a skull that
  // never moved.
  let cursor: string | null = head.bone.parent;
  while (cursor !== null && cursor !== neck.bone.name) {
    cursor = rig.bones.find((b) => b.name === cursor)?.parent ?? null;
  }
  if (cursor === null) {
    throw new HeadSpaceRefusal(
      `"${head.bone.name}" (Head) does not descend from "${neck.bone.name}" (Neck) on "${rig.label}" — the seating cannot reach the skull`,
    );
  }
  return { neck, head };
}

function gate(degrees: number): void {
  if (!Number.isFinite(degrees) || Math.abs(degrees) > HEAD_SPACE_MAX_DEGREES) {
    throw new HeadSpaceRefusal(
      `${degrees}° is outside the ±${HEAD_SPACE_MAX_DEGREES}° band — refused, never clamped silently`,
    );
  }
}

/**
 * Derive the constant offset (one entry, the Neck), or refuse naming why.
 * Zero degrees returns [] — the documented "no filter" state, distinct from
 * a refusal. First-generation bind-frame form: the axis is transported into
 * the Neck's PARENT bind frame; the played-frame door (headSpacePlan +
 * withWorldAxisPreRotation) is what the app ships, this form is what the
 * kernel-only probes compose through.
 */
export function headSpaceOffsets(rig: RigDescriptor, degrees: number): readonly HeadSpaceOffset[] {
  gate(degrees);
  const { neck } = resolveChain(rig);
  if (degrees === 0) return [];

  const { facing, up } = declaredAxes(rig);
  const side = vec3Cross(up, facing); // unit: up ⟂ facing (degenerate combos refused at the rig gate)
  const radians = (degrees * Math.PI) / 180;
  // World side, expressed in the parent's bind frame: pre-multiplying the
  // child local pitches the bone about the character's side axis as carried
  // by the LIVE parent (torso-relative — a bent spine nods in its own plane).
  const parentRotation = quatNormalize(neck.parent.worldBind.rotation);
  const axisInParent = quatRotateVec3(quatInvertUnit(parentRotation), side);
  return [{ bone: neck.bone.name, rotation: quatFromAxisAngle(axisInParent, -radians) }];
}

/**
 * The plan form of {@link headSpaceOffsets}: same gates, same erect-ward
 * sign — output names the WORLD side axis and the signed angle for the
 * played-frame conjugation door (the 2026-08-03 amendment; see
 * WorldAxisRotation in arm-space for the measured why). One entry: the Neck,
 * whose subtree inherits it through the played hierarchy.
 */
export function headSpacePlan(rig: RigDescriptor, degrees: number): readonly WorldAxisRotation[] {
  gate(degrees);
  const { neck } = resolveChain(rig);
  if (degrees === 0) return [];
  const { facing, up } = declaredAxes(rig);
  const side = vec3Cross(up, facing);
  const radians = (degrees * Math.PI) / 180;
  // Erect-ward is the NEGATIVE rotation about side (module header).
  return [{ bone: neck.bone.name, axis: side, radians: -radians }];
}

/**
 * The ARMATURE form (ADR-030): the world rotation the Neck's BIND wears so
 * that the flesh reads `degrees` erect-ward at play — the exact inverse of
 * {@link headSpacePlan}'s play rotation, on the same bone about the same
 * axis. Feed it to reseatBoneBind and its scene twin. Zero degrees returns
 * null after resolving the chain (a broken rig still refuses; a zero still
 * needs a marker — the caller's Loud-Mothball duty).
 *
 * The equivalence this rests on (pinned by the seating probe): a clip that
 * plays a bone at its ORIGINAL bind orientation shows, on the reseated
 * armature, exactly the flesh the display filter showed — for every bone of
 * the subtree, tracked or not. Away from that pose the two differ by the
 * commutator of the seating with the bone's rest-relative motion (ADR-030
 * §11 D2a), which the probe reports rather than assumes away.
 */
export function headSpaceReseat(
  rig: RigDescriptor,
  degrees: number,
): (WorldAxisRotation & { readonly bone: string }) | null {
  gate(degrees);
  const { neck } = resolveChain(rig);
  if (degrees === 0) return null;
  const { facing, up } = declaredAxes(rig);
  const side = vec3Cross(up, facing);
  return { bone: neck.bone.name, axis: side, radians: (degrees * Math.PI) / 180 };
}

/**
 * Rest FORWARD LEAN of the Neck→Head bone: ° from the rig's own up toward its
 * own facing, in the sagittal plane (a lateral component is ignored, not
 * folded in). SIGNED — a segment leaning back reads negative. The unsigned
 * "pitch above horizontal" this replaced (2026-08-27) could not tell forward
 * from back, and the kobold's NeckTwist01→Head segment leans BACK ~9.5°: on
 * it the unsigned delta (+7.5°) was a fraction of the true seating gap, and
 * a reseat that crossed vertical re-measured as a NEW gap on reopen — the
 * signed lean is exact under the reseat (a nod about side by δ adds exactly
 * δ to it), which is what the reopen loop needs. Identical to the old
 * reading on every forward-leaning specimen (android, warrior).
 */
function restLeanDegrees(rig: RigDescriptor): number {
  const { neck, head } = resolveChain(rig);
  const v = vec3Sub(head.bone.worldBind.position, neck.bone.worldBind.position);
  const { up, facing } = declaredAxes(rig);
  const vertical = vec3Dot(v, up);
  const forward = vec3Dot(v, facing);
  if (vertical === 0 && forward === 0) {
    const lateral = vec3Sub(v, vec3Add(vec3Scale(up, vertical), vec3Scale(facing, forward)));
    throw new HeadSpaceRefusal(
      Math.hypot(lateral.x, lateral.y, lateral.z) === 0
        ? `"${neck.bone.name}" and "${head.bone.name}" are co-located on "${rig.label}" — the neck segment has no direction`
        : `"${neck.bone.name}"→"${head.bone.name}" on "${rig.label}" is purely lateral — no sagittal lean to seat`,
    );
  }
  return (Math.atan2(forward, vertical) * 180) / Math.PI;
}

/**
 * The measured per-pair seating delta (°, erect-ward positive): how much
 * further FORWARD the SOURCE leans its Neck→Head bone at rest than the TARGET
 * does. Absolute transport plays the source's bone direction on the target,
 * so this is exactly the head-space value that restores the target's
 * authored flesh reading — the run's DEFAULT reseat, computed with no
 * thresholds (+8.6° android, +13.0° warrior measured; ≈0 for same-family
 * pairs by construction, and ≈0 for a character re-imported from its own
 * seated export, because the reseat moves this very segment by this very
 * angle). Each rig's lean is measured against its OWN declared axes, so a
 * Z-up source and a Y-up target compare cleanly.
 */
export function headSeatingDeltaDegrees(source: RigDescriptor, target: RigDescriptor): number {
  return restLeanDegrees(source) - restLeanDegrees(target);
}

/**
 * The DELTA form of {@link headSpaceReseat}: the same bone, the same world
 * axis, the same sign — but NOT band-gated. The band gates VALUES (a
 * Document's total, a file's declared total); the surgery between two
 * in-band totals is their difference, which may exceed the band (a file
 * seated at −50° declared +50° is a legal 100° turn). Rotation about a fixed
 * axis is linear in the angle and the Neck's origin does not move under a
 * reseat, so the composition of two deltas is the delta of the sum — the
 * app's in-place commit and the run's surgery both come through here.
 * Non-finite refuses; zero returns null after resolving the chain.
 */
export function headSpaceReseatDelta(
  rig: RigDescriptor,
  degrees: number,
): (WorldAxisRotation & { readonly bone: string }) | null {
  if (!Number.isFinite(degrees)) {
    throw new HeadSpaceRefusal(`${degrees}° is not a finite seating delta`);
  }
  const { neck } = resolveChain(rig);
  if (degrees === 0) return null;
  const { facing, up } = declaredAxes(rig);
  const side = vec3Cross(up, facing);
  return { bone: neck.bone.name, axis: side, radians: (degrees * Math.PI) / 180 };
}

// ---------------------------------------------------------------------------
// THE SEATING MARKER (ADR-030 §14). A seated bind cannot say whether a person
// put it there or the character was authored that way: the baseline
// instrument (headSeatingDeltaDegrees) reads the bind alone, so on a
// reopened export it re-measures the gap the seating just closed — and for a
// seating a person pushed PAST the baseline it re-seats the neck back toward
// the source's lean, erasing the declaration. Measured on Allen's fox onesie,
// 2026-08-27: baseline +23.6°, saved at +53.5°, reopened as a −29.9°
// "baseline" and undone. The exported file therefore DECLARES its seating in
// glTF `extras` on the seat bone's node (three carries userData ↔ extras in
// both directions; the file is correct WITHOUT a reader — World never looks —
// and better with one: this app's reopen). The record is the TOTAL the bind
// wears from the authored rest and who decided it: an `explicit` marker makes
// the heuristic stand down on reopen; a `baseline` one lets it re-measure,
// whose residual against the same source is ~0 by construction.
// ---------------------------------------------------------------------------

/** The glTF `extras` namespace this suite writes on nodes it has decided about (poqpoq-extras.ts). */
export const SEATING_EXTRAS_KEY = POQPOQ_EXTRAS_KEY;
/** The key under {@link SEATING_EXTRAS_KEY} that carries the head seating. */
export const HEAD_SEATING_MARKER_KEY = 'headSeating';

export interface HeadSeatingMarker {
  /** TOTAL degrees the Neck bind wears from the authored rest (erect-ward positive). */
  readonly degrees: number;
  /** 'explicit' = a person set it; 'baseline' = the measured default, never confirmed by a person. */
  readonly provenance: 'explicit' | 'baseline';
  /** The source label the seating was measured or tuned against, or null. */
  readonly source: string | null;
}

/**
 * Read the marker out of a node's userData (the loader's copy of `extras`).
 * Absent → null. Present but malformed → a refusal naming where: a file that
 * carries a marker it cannot mean is inconsistent, and reading it as
 * "nothing declared" would silently re-run the heuristic on a declared neck.
 */
export function parseHeadSeatingMarker(userData: unknown, where: string): HeadSeatingMarker | null {
  const refuse = (why: string): never => {
    throw new HeadSpaceRefusal(
      `${where} carries a malformed seating marker (extras.${SEATING_EXTRAS_KEY}.${HEAD_SEATING_MARKER_KEY}): ${why}`,
    );
  };
  const raw = readPoqpoqExtra(userData, HEAD_SEATING_MARKER_KEY);
  if (raw === null) return refuse(`"${SEATING_EXTRAS_KEY}" is not an object`);
  if (raw === undefined) return null;
  if (typeof raw !== 'object') return refuse('not an object');
  const { degrees, provenance, source } = raw as Record<string, unknown>;
  if (typeof degrees !== 'number' || !Number.isFinite(degrees)) return refuse(`degrees ${String(degrees)} is not a finite number`);
  if (Math.abs(degrees) > HEAD_SPACE_MAX_DEGREES) {
    return refuse(`degrees ${degrees} is outside the ±${HEAD_SPACE_MAX_DEGREES}° band`);
  }
  if (provenance !== 'explicit' && provenance !== 'baseline') return refuse(`provenance ${String(provenance)} is neither "explicit" nor "baseline"`);
  if (source !== undefined && source !== null && typeof source !== 'string') return refuse('source is not a string');
  return { degrees, provenance, source: typeof source === 'string' ? source : null };
}

/**
 * A node's userData with the marker written (or, for null, removed) — every
 * other key preserved. Returns a NEW object; the caller assigns it.
 */
export function withHeadSeatingMarker(
  userData: unknown,
  marker: HeadSeatingMarker | null,
): Record<string, unknown> {
  return withPoqpoqExtra(
    userData,
    HEAD_SEATING_MARKER_KEY,
    marker === null ? undefined : { degrees: marker.degrees, provenance: marker.provenance, source: marker.source },
  );
}
